'use client';

import { FC, useMemo, useState } from 'react';
import { Content, isFilled } from '@prismicio/client';
import { SliceComponentProps } from '@prismicio/react';
import { PrismicNextImage, PrismicNextLink } from '@prismicio/next';
import DynamicMarquee from '@/components/DynamicMarquee';
import PageHeroRedesign from '@/components/redesign/PageHeroRedesign';

type RedesignSlice = Extract<
  Content.HeroVelxEngineSlice,
  { variation: 'redesign' }
>;

export type HeroVelxEngineRedesignProps = SliceComponentProps<RedesignSlice>;

/**
 * Subtle edge fade for the logo ribbon: feathers the row's left/right edges so
 * logos scroll in/out cleanly. Applied at every breakpoint since the marquee
 * animates on mobile and desktop alike.
 */
const LOGO_RIBBON_MASK: React.CSSProperties = {
  WebkitMaskImage:
    'linear-gradient(to right, transparent 0, black 10px, black calc(100% - 10px), transparent 100%)',
  maskImage:
    'linear-gradient(to right, transparent 0, black 10px, black calc(100% - 10px), transparent 100%)',
  WebkitMaskRepeat: 'no-repeat',
  maskRepeat: 'no-repeat',
};

/**
 * The logo ribbon must never show the same logo twice at once: one full pass of
 * the unique logos has to be at least as wide as the visible strip, which is
 * capped by the `max-w-[1280px]` container. Each logo gets an even cell sized to
 * `target / logoCount` (with a floor for readability), so the row self-adjusts
 * to any number of logos — few logos spread out, many logos stay compact — and
 * a full set always overflows the viewport before it can repeat.
 */
const LOGO_RIBBON_TARGET_WIDTH = 1320; // ≥ the max-w-[1280px] container cap
const LOGO_CELL_MIN_WIDTH = 160; // floor so dense strips stay legible

const HeroVelxEngineRedesign: FC<HeroVelxEngineRedesignProps> = ({ slice }) => {
  const {
    kicker,
    logo,
    title,
    description,
    cta_link,
    cta_anchor,
    background_image,
    background_video,
    background_video_poster,
    logos_title,
    logos,
  } = slice.primary;

  // Ribbon animation state, replicated from the default HeroVelxEngine variant:
  // pause on hover and loop a doubled set of logos for a seamless marquee.
  const [isPaused, setIsPaused] = useState(false);
  const logoItems = useMemo(
    () => (logos ?? []).filter((item) => isFilled.image(item.logo)),
    [logos],
  );
  const doubledLogos = useMemo(
    () => (logoItems.length > 0 ? [...logoItems, ...logoItems] : []),
    [logoItems],
  );
  // Even per-logo cell width so one full set always overflows the strip and no
  // logo repeats within view, whatever the logo count.
  const logoCellMinWidth =
    logoItems.length > 0
      ? Math.max(
          LOGO_CELL_MIN_WIDTH,
          Math.ceil(LOGO_RIBBON_TARGET_WIDTH / logoItems.length),
        )
      : LOGO_CELL_MIN_WIDTH;

  return (
    <div
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
    >
      <PageHeroRedesign
        eyebrow={kicker}
        logo={logo}
        title={title}
        description={description}
        ctaLink={cta_link}
        ctaAnchor={cta_anchor}
        backgroundImage={background_image}
        backgroundVideo={background_video}
        backgroundVideoPoster={background_video_poster}
      >
        {logoItems.length > 0 && (
          <div className="relative bg-[#262626] px-5 py-8 md:px-[30px] lg:py-[30px]">
            <div className="mx-auto flex w-full max-w-[1280px] flex-col items-start gap-8 lg:gap-[35px]">
              {logos_title && (
                <p className="w-full text-lg font-medium leading-[1.4] text-[#aaaaaa] md:text-[26px] lg:text-[30px]">
                  {logos_title}
                </p>
              )}
              <div className="relative w-full" style={LOGO_RIBBON_MASK}>
                <DynamicMarquee speed={30} gradient={false} play={!isPaused}>
                  {doubledLogos.map((item, index) => {
                    const logoImage = (
                      <PrismicNextImage
                        field={item.logo}
                        className="h-[28px] w-auto object-contain opacity-70 transition-opacity duration-200 hover:opacity-100"
                        fallbackAlt=""
                      />
                    );

                    return (
                      <div
                        key={`logo-${index}`}
                        className="flex h-[52px] shrink-0 items-center justify-center px-8"
                        style={{ minWidth: `${logoCellMinWidth}px` }}
                        onMouseEnter={() => setIsPaused(true)}
                        onMouseLeave={() => setIsPaused(false)}
                      >
                        {isFilled.link(item.logo_link) ? (
                          <PrismicNextLink
                            field={item.logo_link}
                            aria-label="View client case study"
                          >
                            {logoImage}
                          </PrismicNextLink>
                        ) : (
                          logoImage
                        )}
                      </div>
                    );
                  })}
                </DynamicMarquee>
              </div>
            </div>
          </div>
        )}
      </PageHeroRedesign>
    </div>
  );
};

export default HeroVelxEngineRedesign;
